/**
 * Profile export — wrapper builder + browser download (ADR-033
 * §11.1).
 *
 * Export is purely client-side: fetch the Profile document (via
 * :func:`getProfile`), wrap it in the documented
 * ``meridian_profile_export`` envelope, and trigger a browser
 * download of ``{name}.meridian-profile.json``. All roles may
 * export any Profile they can read (REQ-UI-081).
 *
 * The wrapper builder (:func:`buildProfileExport`) is a pure
 * function so it is unit-testable without a DOM; the download
 * trigger (:func:`downloadJson`) isolates the one impure step
 * (Blob + object-URL + synthetic anchor click) behind a small
 * surface that the provider calls.
 *
 * Cross-references:
 *   - REQ-UI-081 (export to the documented wrapper format).
 *   - ADR-033 §11.1 (envelope shape).
 */
import type { Network, ProfileDetail } from "@/auth/session-types";

/** Current export envelope version (ADR-033 §11.1). */
export const EXPORT_FORMAT_VERSION = 1 as const;

/** The set of characters disallowed (or awkward) in a download
 *  filename stem, collapsed to a single hyphen by
 *  :func:`profileExportFilename`. Defined as a single-pass regex
 *  with the hyphen anchored last so it cannot form an accidental
 *  character-class range. */
const FILENAME_RESERVED = /[/\\:*?"<>|-]+/g;

/**
 * The ``meridian_profile_export`` envelope (ADR-033 §11.1). The
 * ``source_network`` is informational (the exporting deployment's
 * classification); ``scope_at_export`` is informational too —
 * import lets the user choose the target scope.
 */
export interface ProfileExportWrapper {
  meridian_profile_export: {
    export_format_version: typeof EXPORT_FORMAT_VERSION;
    exported_at: string;
    source_network: string;
    profile: {
      name: string;
      scope_at_export: "global" | "user";
      profile_schema_version: number;
      document: ProfileDetail["document"];
    };
  };
}

/**
 * Build the export envelope for a fetched Profile. Pure — the
 * ``exportedAt`` clock is injected so callers (and tests) control
 * the timestamp.
 *
 * ``sourceNetwork`` is the deployment designation (``a`` / ``b`` /
 * ``c``); it is upper-cased in the envelope to match the form the
 * spec shows (``"A"``), purely as the informational
 * ``source_network`` field — it carries no
 * cross-network semantics (Profiles are per-network; ADR-033 §1.3).
 *
 * REQ-UI-081; ADR-033 §11.1.
 */
export function buildProfileExport(
  profile: ProfileDetail,
  options: { sourceNetwork: Network; exportedAt: string },
): ProfileExportWrapper {
  return {
    meridian_profile_export: {
      export_format_version: EXPORT_FORMAT_VERSION,
      exported_at: options.exportedAt,
      source_network: options.sourceNetwork.toUpperCase(),
      profile: {
        name: profile.name,
        scope_at_export: profile.scope,
        profile_schema_version: profile.document.profile_schema_version,
        document: profile.document,
      },
    },
  };
}

/**
 * Derive the download filename for a Profile. Sanitises the
 * Profile name into a filesystem-safe stem (the user-chosen name
 * can contain slashes, colons, etc.) and appends the
 * ``.meridian-profile.json`` suffix the format prescribes.
 *
 * Whitespace is collapsed first, then the reserved set, then stray
 * leading/trailing hyphens are trimmed. An empty/blank name falls
 * back to a stable stem rather than producing a dotfile.
 *
 * ADR-033 §11.1 (``{name}.meridian-profile.json``).
 */
export function profileExportFilename(name: string): string {
  const stem = name
    .trim()
    .replace(/\s+/g, "-")
    .replace(FILENAME_RESERVED, "-")
    .replace(/^-+|-+$/g, "");
  return `${stem.length > 0 ? stem : "profile"}.meridian-profile.json`;
}

/**
 * Trigger a browser download of ``value`` as pretty-printed JSON
 * under ``filename``. Isolates the impure DOM steps (Blob,
 * ``URL.createObjectURL``, synthetic anchor click, revoke) so the
 * rest of the export path stays pure + testable.
 *
 * No-ops outside a browser (``document`` absent) so the call is
 * safe in SSR / test contexts that don't stub the DOM.
 *
 * REQ-UI-035 — this is entirely client-side; it makes no network
 * call (the document is already in hand).
 */
export function downloadJson(filename: string, value: unknown): void {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
    return;
  }
  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    // Appending is required for the click to fire in some browsers;
    // remove immediately after so the DOM is left untouched.
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}
